import React, { useState } from 'react';
import { 
  Box,
  Container, 
  TextField, 
  Button, 
  Typography, 
  Paper, 
  CircularProgress,
  Alert,
  Link
} from '@mui/material';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const ResetPassword = () => {
  const { uid, token } = useParams();
  const navigate = useNavigate();
  const { confirmPasswordReset } = useAuth();
  
  const [formData, setFormData] = useState({
    new_password: '',
    re_new_password: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    
    // Check if passwords match
    if (formData.new_password !== formData.re_new_password) {
      setErrorMessage('Passwords do not match');
      return;
    }
    
    setLoading(true);
    
    try {
      await confirmPasswordReset({
        uid,
        token,
        new_password: formData.new_password,
        re_new_password: formData.re_new_password
      });
      
      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      setErrorMessage(
        err.response?.data?.detail || 
        err.response?.data?.non_field_errors?.[0] || 
        'Failed to reset password. The link may have expired.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh'
        }}
      >
        <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
          <Typography component="h1" variant="h5" align="center" gutterBottom>
            Accrual Management System
          </Typography>
          
          <Typography component="h2" variant="h6" align="center" mb={3}>
            Set New Password
          </Typography>
          
          {success ? (
            <Box>
              <Alert severity="success" sx={{ mb: 2 }}>
                Password has been reset successfully! Redirecting to login...
              </Alert>
            </Box>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: 3 }}>
                Enter your new password below.
              </Typography>
              
              {errorMessage && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {errorMessage}
                </Alert>
              )}
              
              <Box component="form" onSubmit={handleSubmit} noValidate>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="new_password"
                  label="New Password"
                  type="password"
                  id="new_password"
                  autoComplete="new-password"
                  value={formData.new_password}
                  onChange={handleChange}
                  disabled={loading}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="re_new_password"
                  label="Confirm New Password"
                  type="password"
                  id="re_new_password"
                  autoComplete="new-password"
                  value={formData.re_new_password}
                  onChange={handleChange}
                  disabled={loading}
                  error={formData.new_password !== formData.re_new_password && formData.re_new_password !== ''}
                  helperText={
                    formData.new_password !== formData.re_new_password && formData.re_new_password !== ''
                      ? 'Passwords do not match'
                      : ''
                  }
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2 }}
                  disabled={loading || !formData.new_password || !formData.re_new_password}
                >
                  {loading ? <CircularProgress size={24} /> : 'Reset Password'}
                </Button>
                
                <Typography variant="body2" align="center">
                  <Link component={RouterLink} to="/login" underline="hover">
                    Back to login
                  </Link>
                </Typography>
              </Box>
            </>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default ResetPassword; 